import { useState } from 'react'
import { ChevronDown, Info } from 'lucide-react'
import { Badge } from '@/components/shadcn/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import { IconBg } from '@/components/ui/IconBg'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { IconOpenInNewOutline } from '@icons/IconOpenInNewOutline'
import { cn } from '@/lib/utils'
import { getBadgeColor } from '../CardsComponent.tsx/utils'
import { StatusType } from '../CardsComponent.tsx/types'
import {
  ListCard,
  ListCardAction,
  ListCardColumn,
  ListCardInfoBanner,
  ListCardStatus,
  ListCardStatusValue,
  ListCardUI,
} from './types'
import { ListCardMarkdown } from './ListCardMarkdown'
import { ListCardActionButton } from './ListCardActionButton'
import { ListCardHoverMetadataIcon } from './ListCardHoverMetadataIcon'

function getPendingBadgeColor() {
  return 'bg-neutral-50 text-neutral-600 border border-neutral-300'
}

function getStatusBadgeClassName(statusType: ListCardStatusValue) {
  if (statusType === 'pending') return getPendingBadgeColor()
  return getBadgeColor(statusType as StatusType)
}

function getInfoBannerClassName(status: StatusType) {
  switch (status) {
    case 'error':
      return 'border-statusred-300 bg-statusred-50'
    case 'warning':
      return 'border-statusyellow-300 bg-statusyellow-50'
    case 'success':
      return 'border-statusgreen-300 bg-statusgreen-50'
    case 'info':
      return 'border-statusblue-300 bg-statusblue-50'
    default:
      return 'border-statusblue-300 bg-statusblue-50'
  }
}

function getInfoBannerIconClassName(status: StatusType) {
  switch (status) {
    case 'error':
      return 'text-statusred-500'
    case 'warning':
      return 'text-statusyellow-600'
    case 'success':
      return 'text-statusgreen-600'
    case 'info':
      return 'text-statusblue-500'
    default:
      return 'text-statusblue-500'
  }
}

function ListCardInfoBannerContent({ banner }: { banner: ListCardInfoBanner }) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 rounded-md border px-4 py-3',
        getInfoBannerClassName(banner.status)
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <Info className={cn('mt-0.5 size-4 shrink-0', getInfoBannerIconClassName(banner.status))} />
        <div className="min-w-0">
          {banner.title && <p className="text-sm font-semibold text-foreground">{banner.title}</p>}
          <p className="text-sm text-muted-foreground">{banner.text}</p>
        </div>
      </div>
      {banner.bannerAction && <ListCardActionButton action={banner.bannerAction} appearance="link" />}
    </div>
  )
}

function ListCardColumnContent({ column }: { column: ListCardColumn }) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {column.title && <h4 className="text-sm font-semibold text-foreground">{column.title}</h4>}
      {column.type === 'markdown' && <ListCardMarkdown content={column.content} />}
      {column.type === 'external-links' && (
        <ul className="flex flex-col gap-2">
          {column.content.map((link) => (
            <li key={`${link.label}-${link.url}`}>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-2 hover:text-brandorange-500"
              >
                {link.label}
                <IconOpenInNewOutline className="size-3.5 shrink-0" />
              </a>
            </li>
          ))}
        </ul>
      )}
      {column.type === 'key-value-rows' && (
        <>
          <ul className="flex flex-col divide-y divide-border">
            {column.content.map((row) => (
              <li key={`${row.label}-${row.value}`} className="flex items-center gap-3 py-3">
                {row.icon && <DynamicIcon iconName={row.icon} className="size-4 shrink-0 text-muted-foreground" />}
                <span className="min-w-0 flex-1 text-sm text-foreground">{row.label}</span>
                <span className="shrink-0 text-right text-sm text-foreground">{row.value}</span>
              </li>
            ))}
          </ul>
          {column.actions && column.actions.length > 0 && (
            // <div className="flex flex-col gap-2">
            <div className="flex flex-wrap justify-center gap-2">
              {column.actions
                .filter((action) => action.isVisible)
                .map((action) => (
                  // <ListCardActionButton key={action.id} action={action} />
                  <ListCardActionButton className="w-max" key={action.id} action={action} />
                ))}
            </div>
          )}
        </>
      )}
      {column.type === 'actions' && (
        <div className="flex flex-col gap-2">
          {column.content
            .filter((action) => action.isVisible)
            .map((action) => (
              <ListCardActionButton key={action.id} action={action} />
            ))}
          {column.metadata && <p className="text-xs text-muted-foreground">{column.metadata}</p>}
        </div>
      )}
    </div>
  )
}

function ListCardBody({ cardUI, cardData }: { cardUI: ListCardUI; cardData: NonNullable<ListCard['cardData']> }) {
  const { infoBanner, columns } = cardData

  return (
    <div className="flex flex-col gap-5 border-t bg-card px-5 pb-5 pt-4">
      {infoBanner && <ListCardInfoBannerContent banner={infoBanner} />}
      {columns.length > 0 && (
        <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
          {columns.map((column, index) => (
            <div
              key={`${cardUI.id}-column-${index}`}
              className={cn('min-w-0', index > 0 && 'border-l border-border pl-6')}
            >
              <ListCardColumnContent column={column} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ListCardHeaderActions({ actions }: { actions: ListCardAction[] }) {
  const visibleActions = actions.filter((action) => action.isVisible)
  if (visibleActions.length === 0) return null

  return (
    <div className="flex shrink-0 items-center gap-1">
      {visibleActions.map((action) => (
        <ListCardActionButton key={action.id} action={action} appearance="icon" />
      ))}
    </div>
  )
}

function ListCardTitleBlock({ cardUI }: { cardUI: ListCardUI }) {
  return (
    <>
      {cardUI.icon && (
        <IconBg className="size-12 rounded-full bg-secondary text-primary">
          <DynamicIcon iconName={cardUI.icon} className="size-6" />
        </IconBg>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-4">
          <p className="text-base font-bold text-foreground">{cardUI.title}</p>
          <ListCardHoverMetadataIcon hoverMetadata={cardUI.hoverMetadata} />
        </div>
        {cardUI.description && <p className="text-sm text-muted-foreground">{cardUI.description}</p>}
      </div>
    </>
  )
}

function ListCardStatusBadge({ status }: { status: ListCardStatus }) {
  return (
    <Badge className={cn(getStatusBadgeClassName(status.type), 'shrink-0 text-xs font-semibold shadow-none')}>
      {status.label}
    </Badge>
  )
}

function ListCardHeader({
  cardUI,
  status,
  headerActions,
  isCollapsible,
  isOpen,
}: {
  cardUI: ListCardUI
  status?: ListCardStatus
  headerActions?: ListCardAction[]
  isCollapsible: boolean
  isOpen: boolean
}) {
  const actions = headerActions && headerActions.length > 0 ? <ListCardHeaderActions actions={headerActions} /> : null
  const statusBadge = status ? <ListCardStatusBadge status={status} /> : null
  const chevron = isCollapsible ? (
    <ChevronDown
      className={cn('size-5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-180')}
    />
  ) : null

  if (isCollapsible) {
    return (
      <div className="flex w-full items-center gap-4 px-5 py-4 hover:bg-muted/30">
        <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-4 text-left">
          <ListCardTitleBlock cardUI={cardUI} />
        </CollapsibleTrigger>
        {actions}
        <CollapsibleTrigger className="flex shrink-0 items-center gap-4">
          {statusBadge}
          {chevron}
        </CollapsibleTrigger>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-4 px-5 py-4">
      <ListCardTitleBlock cardUI={cardUI} />
      {actions}
      {statusBadge}
    </div>
  )
}

export function isListCardCollapsible(card: ListCard) {
  const { cardUI, cardData } = card
  const hasCardData = !!cardData && (cardData.columns.length > 0 || !!cardData.infoBanner)
  return cardUI.isCollapsible && hasCardData
}

export function ListCardItem({
  card,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  card: ListCard
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const { cardUI, cardData } = card
  const hasCardData = !!cardData && (cardData.columns.length > 0 || !!cardData.infoBanner)
  const isCollapsible = isListCardCollapsible(card)
  const [internalIsOpen, setInternalIsOpen] = useState(cardUI.defaultExpanded ?? true)
  const isOpen = controlledIsOpen ?? internalIsOpen
  const setIsOpen = controlledOnOpenChange ?? setInternalIsOpen

  const body = hasCardData ? <ListCardBody cardUI={cardUI} cardData={cardData} /> : null

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} disabled={!isCollapsible}>
      <div className="overflow-hidden rounded-lg border border-border bg-offwhite shadow-sm">
        <ListCardHeader
          cardUI={cardUI}
          status={cardData?.status}
          headerActions={cardData?.headerActions}
          isCollapsible={isCollapsible}
          isOpen={isOpen}
        />
        {body &&
          (isCollapsible ? (
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
              {body}
            </CollapsibleContent>
          ) : (
            body
          ))}
      </div>
    </Collapsible>
  )
}
